import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';

// Hooks
import SystemDataService from '../../Services/SystemDataService';
import useDisableScrolling from '../../Hooks/useDisableScrolling';
import useClickOutside from '../../Hooks/useClickOutside';
import useScrollOrNavigate from '../../Hooks/useScrollOrNavigate'; // YOUR HOOK

// UI Components
import { Modal } from './modal-component';
import { ButtonAnimatedWhite, ButtonChevronReveal, ButtonPrimary } from '../Buttons/button-component';
import MediaComponent from '../Media/media-component';

const COOKIE_NAME = 'promo_popup_dismissed_v1';


const PopUpPromotion = ({ expand = true, delay = 4000 }) => {
    const systemData = useSelector((state) => state.systemData || {});
    const { language = 'he', dir = 'rtl' } = systemData;
    const phone = SystemDataService.byLabel(systemData?.links, 'Phone');

    const [config, setConfig] = useState(null);
    const [eligible, setEligible] = useState(false); 
    const [delayedOk, setDelayedOk] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    
    const modalRef = useRef(null);
    const scrollOrNavigate = useScrollOrNavigate();

    const labels = {
        he: { call: 'התקשרו עכשיו', learnMore: 'למידע נוסף', defaultTitle: 'מבצע מיוחד!' },
        en: { call: 'Call Now', learnMore: 'Learn More', defaultTitle: 'Special Offer!' }
    };

    const t = labels[language] || labels.en;

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const data = await SystemDataService.getPopUpImage();
                // STRICT CHECK: If no data, no image, or status is false, do nothing.
                if (!mounted || !data || !data[0] || !data[0].image || data[0].status === false) {
                    return; 
                }
                
                setConfig(data[0]);
                const hasBeenDismissed = Cookies.get(COOKIE_NAME);
                setEligible(!hasBeenDismissed);
            } catch (e) {
                console.error('PopUp Config Error:', e);
            }
        })();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (expand && eligible && !dismissed && config) {
            const timer = setTimeout(() => setDelayedOk(true), delay);
            return () => clearTimeout(timer);
        }
    }, [expand, eligible, dismissed, delay, config]);

    const handleDismiss = useCallback(() => {
        Cookies.set(COOKIE_NAME, 'true', { expires: 1 / 24 });
        setDismissed(true);
        setEligible(false);
    }, []);

    const handleActionClick = (e) => {
        handleDismiss();
        const target = config?.actionButton || 'contact-form';
        if (target.startsWith('http')) {
            window.open(target, '_blank', 'noopener');
            return;
        }
        scrollOrNavigate(e, 'contact-form', target.startsWith('/') ? target : `/${target}`);
    };

    useClickOutside(modalRef, handleDismiss);

    // Visibility condition
    const isOpen = expand && eligible && delayedOk && !dismissed && config;
    useDisableScrolling(isOpen);

    // CRITICAL: If no config is loaded, or user isn't eligible, return null immediately (no fallback UI)
    if (!config || !eligible || !config.image) return null;

    return (
        <Modal expand={isOpen} timeout={false}>
            <div dir={dir} className="flex w-full h-full justify-center items-center p-4 font-assistant">
                <div 
                    ref={modalRef}
                    className="relative w-full max-w-lg overflow-hidden bg-white dark:bg-neutral-900 rounded-[2.5rem] shadow-2xl animate-modal-in"
                >
                    <button
                        onClick={handleDismiss}
                        className={`absolute top-5 ${dir === 'rtl' ? 'left-5' : 'right-5'} z-20 rounded-full bg-black/5 hover:bg-black/10 p-2 transition-all hover:rotate-90 text-gray-800 dark:text-white dark:bg-white/10`}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>

                    <div className="flex flex-col">
                        <div className="w-full h-64 md:h-80 relative bg-gray-50 dark:bg-neutral-800">
                            {/* MEDIA ONLY MOUNTS/PLAYS WHEN VISIBLE */}
                                <MediaComponent
                                    src={config.image}
                                    alt={config.title || 'Promotion'}
                                    width={800}
                                    quality={70}
                                    wrapperClassName="w-full h-full"
                                    autoPlayWidth={1} 
                                />
                        </div>

                        <div className="p-8 md:p-10 flex flex-col gap-6 text-center">
                            <div className="space-y-3">
                                <h3 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white leading-[1.1]">
                                    {config.title || t.defaultTitle}
                                </h3>
                                {config.subtitle && (
                                    <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 font-medium leading-relaxed">
                                        {config.subtitle}
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col gap-3 pt-4">
                                {config.isCall && phone?.url && (
                                    <ButtonPrimary
                                        size="lg"
                                        fullWidth
                                        href={phone.url} 
                                        onClick={handleDismiss}
                                        className="!rounded-2xl shadow-xl shadow-blue-500/20 active:scale-95 transition-transform"
                                    >
                                        <span className="text-xl font-bold">{t.call}</span>
                                    </ButtonPrimary>
                                )}

                                {config.isActionButton && (
                                    <ButtonChevronReveal
                                        size="lg"
                                        fullWidth
                                        onClick={handleActionClick}
                                        className="!rounded-2xl border-2 active:scale-95 transition-transform"
                                    >
                                        <span className="text-lg font-bold">{t.learnMore}</span>
                                    </ButtonChevronReveal>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PopUpPromotion;